"use client";
import { createContext, useContext, type ReactNode } from "react";

/**
 * The bbtest variant ids active for this render.
 *
 * Resolved once per request on the server (`lib/bbTests/resolveBbTests.ts`,
 * behind the `BB_TEST_VARIANTS_ENABLED` gate) and handed to this provider by
 * `ExperienceWrapper`. Components that vary by behaviour rather than by tree
 * shape (a scheduler phase order, a phone-first button) read `useBbTest(id)`
 * and render control when it is false. The default value is the empty list,
 * so a component rendered outside the provider, on another route or in a
 * test, is always control.
 */
const BbTestContext = createContext<readonly string[]>([]);
BbTestContext.displayName = "AspenMarketingBbTestContext";

export const BbTestProvider = ({
	activeIds,
	children,
}: {
	activeIds: readonly string[];
	children: ReactNode;
}) => <BbTestContext.Provider value={activeIds}>{children}</BbTestContext.Provider>;

/** Every active variant id, in registry order. Empty for control. */
export const useBbTests = (): readonly string[] => useContext(BbTestContext);

/** True when this variant is active, e.g. `useBbTest("a05a")`. */
export const useBbTest = (variantId: string): boolean =>
	useContext(BbTestContext).includes(variantId);
